import type { StickerRef } from '@/data/db';
import rawDefinitions from './sticker-definitions.json';
import type { StickerColorToken, StickerDefinitionData, StickerShape } from './sticker-shapes';

/**
 * 內建的原創 SVG 貼紙（spec 21.7）。
 *
 * 圖案本身定義在 `sticker-definitions.json`（開發者維護的資料檔），這裡只負責把
 * 形狀描述轉成 SVG。要新增貼紙，在該 JSON 檔追加一筆即可，不需要改這支檔案。
 *
 * 辦公與生活主題，色調沿用晨光莓果與月夜可可雙色系，
 * Light 與 Dark 下都要看得清楚，所以一律用語意色票而不是寫死顏色。
 * 這些是**使用者內容**，與系統介面的 Phosphor 圖示分開。
 */

export type StickerDefinition = StickerDefinitionData;

export const STICKER_LIBRARY: readonly StickerDefinition[] = rawDefinitions as StickerDefinition[];

const BY_KEY = new Map(STICKER_LIBRARY.map((sticker) => [sticker.key, sticker]));

/** 語意色票 → CSS 變數，供 Light/Dark 雙主題自動切換（spec 21.7）。 */
const COLOR_VARS: Record<Exclude<StickerColorToken, 'none'>, string> = {
  accent: 'var(--accent)',
  accentStrong: 'var(--accent-strong)',
  soft: 'var(--accent-soft)',
  warning: 'var(--warning)',
  success: 'var(--success)',
  danger: 'var(--danger)',
  muted: 'var(--text-muted)',
  surface: 'var(--surface-raised)',
};

function resolveColor(token: StickerColorToken | undefined): string {
  if (!token || token === 'none') return 'none';
  return COLOR_VARS[token];
}

function renderShape(shape: StickerShape, key: number) {
  const fill = resolveColor(shape.fill);
  const stroke = resolveColor(shape.stroke);
  const common = {
    fill,
    stroke,
    strokeWidth: shape.strokeWidth ?? 1.5,
    strokeLinecap: shape.strokeLinecap,
    strokeLinejoin: shape.strokeLinejoin,
    opacity: shape.opacity,
  };

  switch (shape.type) {
    case 'path':
      return <path key={key} d={shape.d} {...common} />;
    case 'circle':
      return <circle key={key} cx={shape.cx} cy={shape.cy} r={shape.r} {...common} />;
    case 'ellipse':
      return (
        <ellipse key={key} cx={shape.cx} cy={shape.cy} rx={shape.rx} ry={shape.ry} {...common} />
      );
    case 'rect':
      return (
        <rect
          key={key}
          x={shape.x}
          y={shape.y}
          width={shape.width}
          height={shape.height}
          rx={shape.rx}
          {...common}
        />
      );
    case 'line':
      return <line key={key} x1={shape.x1} y1={shape.y1} x2={shape.x2} y2={shape.y2} {...common} />;
  }
}

export function svgSticker(key: string): StickerRef {
  return `svg:${key}`;
}

export function emojiSticker(emoji: string): StickerRef {
  return `emoji:${emoji}`;
}

export function parseSticker(ref: StickerRef): { kind: 'svg' | 'emoji'; value: string } | null {
  if (ref.startsWith('svg:')) return { kind: 'svg', value: ref.slice(4) };
  if (ref.startsWith('emoji:')) return { kind: 'emoji', value: ref.slice(6) };
  return null;
}

export function stickerName(ref: StickerRef): string {
  const parsed = parseSticker(ref);
  if (!parsed) return '貼紙';
  if (parsed.kind === 'emoji') return parsed.value;
  return BY_KEY.get(parsed.value)?.name ?? '貼紙';
}

/* oxlint-disable jsx-a11y/prefer-tag-over-role -- 內嵌 SVG 與 emoji 文字沒辦法換成 img 標籤，role="img" 才是正確做法 */
/** 貼紙純屬裝飾，不得成為任何狀態的唯一表示方式（spec 21.7）。 */
export function Sticker({ sticker, size = 18 }: { sticker: StickerRef; size?: number }) {
  const parsed = parseSticker(sticker);
  if (!parsed) return null;

  if (parsed.kind === 'emoji') {
    return (
      <span
        role="img"
        aria-label={parsed.value}
        style={{ fontSize: size }}
        className="leading-none"
      >
        {parsed.value}
      </span>
    );
  }

  const definition = BY_KEY.get(parsed.value);
  if (!definition) return null;

  return (
    <svg
      role="img"
      aria-label={definition.name}
      viewBox="0 0 24 24"
      width={size}
      height={size}
      className="shrink-0"
    >
      {definition.shapes.map((shape, index) => renderShape(shape, index))}
    </svg>
  );
}

/* oxlint-enable jsx-a11y/prefer-tag-over-role */

/** 使用者自己貼在內容上的 emoji 屬於使用者內容，允許使用（spec 21.1）。 */
export const EMOJI_CHOICES = [
  '📌',
  '✅',
  '⭐',
  '🔥',
  '💡',
  '📞',
  '📧',
  '📝',
  '📅',
  '⏰',
  '💰',
  '🧾',
  '🏢',
  '🚗',
  '🎁',
  '☕',
  '🌸',
  '❤️',
  '😊',
  '🙏',
] as const;
